import {DestroyRef, inject, Injectable, OnDestroy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, EMPTY, from, map, Observable, Subject, switchMap, tap, throwError} from 'rxjs';
import {Store} from '@ngrx/store';
import {Environment} from '@environment/environment.type';
import {SnackBarActions} from '@store/aviary/aviary.adapter';
import {SquareAppDetails} from '@store/balance/balance.types';

interface SquareApi {
  payments: (appId: string, locationId: string) => SquarePayments;
}

interface SquarePayments {
  card: () => Promise<SquareCard>;
}

interface SquareCard {
  attach(containerId: string): Promise<void>;

  tokenize(): Promise<SquareCard>;

  token?: string;
  details?: {
    card: {
      last4: string;
    }
  };
}

export interface SquareCardToken {
  token: string;
  last4: string;
}

@Injectable({providedIn: 'any'})
export class SquarePaymentService implements OnDestroy {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly loadSquareScriptListener = () => this.initializeSquare();
  private readonly store = inject(Store);
  private readonly environment = inject(Environment);
  private squareScript: HTMLElement | null;
  private card: SquareCard;
  private squareAppDetails: SquareAppDetails;
  public ready = new Subject<boolean>();

  public setSquareAppDetails(squareAppDetails: SquareAppDetails): void {
    this.squareAppDetails = squareAppDetails;
  }

  public loadSquareScript(): void {
    if (!this.environment.squareWebSdkUrl) return;
    const id = 'squareScript';
    const body: HTMLElement = document.body;
    this.squareScript = document.getElementById(id);
    if (this.squareScript === null) {
      this.squareScript = document.createElement('script');
      this.squareScript.setAttribute('id', id);
      this.squareScript.setAttribute('src', this.environment.squareWebSdkUrl);
      body.appendChild(this.squareScript);
      this.squareScript.addEventListener('load', this.loadSquareScriptListener);
    } else {
      this.initializeSquare();
    }
  }

  public initializeSquare(): void {
    const squareApi: SquareApi = window['Square'];
    if (!squareApi) {
      throw new Error('Square.js failed to load properly');
    }
    if (!this.squareAppDetails) {
      throw new Error('App details not configured');
    }
    const payments = squareApi.payments(this.squareAppDetails.appId, this.squareAppDetails.locationId);
    from(payments.card())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((card) => this.card = card),
        switchMap((card) => from(card.attach('#card-container')).pipe(
          catchError(() => {
            this.store.dispatch(SnackBarActions.showError('Failed to load Square Payment.'))
            return EMPTY;
          })
        )),
        tap(() => this.ready.next(true))
      ).subscribe();
  }

  public submit(): Observable<SquareCardToken> {
    if (!this.card) return throwError(() => new Error('Square payment form is not ready'));

    this.ready.next(false);

    return from(this.card.tokenize()).pipe(
      map((result: SquareCard) => {
        if (!result.token) throw new Error('Square did not return a payment token');

        return {
          token: result.token,
          last4: result.details?.card?.last4 ?? ''
        };
      }),
      catchError((error: unknown) => {
        this.store.dispatch(SnackBarActions.showError('Invalid payment details.'));
        this.ready.next(true);
        return throwError(() => error);
      })
    );
  }

  public ngOnDestroy(): void {
    this.squareScript?.removeEventListener('load', this.loadSquareScriptListener);
  }
}
